
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { 
  PurchaseRequest, 
  inventoryCategoryOptions, 
  purchaseRequestStatusOptions,
  getPurchaseRequestStatusColor
} from "./inventoryTypes";
import { fetchPurchaseRequests } from "./inventoryService";
import { Search } from "lucide-react";
import { PurchaseRequestDialog } from "./PurchaseRequestDialog";
import { NewPurchaseRequestDialog } from "./NewPurchaseRequestDialog";

export function PurchaseRequestList() {
  const { data: purchaseRequests = [], isLoading, error, refetch } = useQuery({
    queryKey: ["purchaseRequests"],
    queryFn: fetchPurchaseRequests,
  });

  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);

  const filteredRequests = purchaseRequests.filter((request) => {
    if (!search) return true;
    
    const searchLower = search.toLowerCase();
    return (
      request.description?.toLowerCase().includes(searchLower) ||
      request.requester_name?.toLowerCase().includes(searchLower) ||
      request.category.toLowerCase().includes(searchLower) ||
      request.id.toLowerCase().includes(searchLower)
    );
  });

  const handleRequestClick = (request: PurchaseRequest) => {
    setSelectedRequest(request);
  };

  const handleDialogClose = (updated: boolean) => {
    setSelectedRequest(null);
    if (updated) {
      refetch();
    }
  };

  const handleNewRequestDialogClose = (created: boolean) => {
    setShowNewRequestDialog(false);
    if (created) {
      refetch();
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "MMM d, yyyy");
  };

  if (isLoading) return <div className="p-8 text-center">Loading purchase requests...</div>;

  if (error) return <div className="p-8 text-center text-red-500">Error loading purchase requests</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-80">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search requests..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button onClick={() => setShowNewRequestDialog(true)}>
          New Purchase Request
        </Button>
      </div>
      
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Requester</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Estimated Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  No purchase requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
                <TableRow 
                  key={request.id}
                  onClick={() => handleRequestClick(request)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="font-medium">
                    {request.description || "No description"}
                  </TableCell>
                  <TableCell>{request.category}</TableCell>
                  <TableCell>{request.requester_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getPurchaseRequestStatusColor(request.status)}>
                      {request.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(request.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {request.estimated_cost ? `${request.currency} ${request.estimated_cost}` : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectedRequest && (
        <PurchaseRequestDialog
          request={selectedRequest}
          open={!!selectedRequest}
          onClose={handleDialogClose}
        />
      )}

      {showNewRequestDialog && (
        <NewPurchaseRequestDialog
          open={showNewRequestDialog}
          onClose={handleNewRequestDialogClose}
        />
      )}
    </div>
  );
}
